<template>
  <v-dialog
    v-model="openCalendarDialog"
    max-width="1400px"
    persistent
    content-class="calendar-dialog"
  >
    <div
      class="calendar-container"
      :class="showEventForm ? '' : 'big-container'"
    >
      <v-row justify="space-between">
        <v-tabs
          class="calendar-tabs"
          color="secondary"
          v-model="tab"
          :show-arrows="false"
        >
          <v-tab>{{ $t('calendar.title') }}</v-tab>
          <v-tab>{{ $t('calendar.informs') }}</v-tab>
        </v-tabs>
        <div>
          <v-btn color="secondary" icon @click="closeDialog">
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </div>
      </v-row>
      <v-tabs-items v-model="tab" :touchless="true">
        <v-tab-item class="calendar-tab-content">
          <div>
            <v-sheet height="613" class="data-loading" v-show="loadingEvents">
              <v-progress-circular
                :size="70"
                :width="7"
                color="blue"
                indeterminate
              ></v-progress-circular>
            </v-sheet>
            <div v-show="!loadingEvents">
              <v-col v-if="!showEventForm">
                <v-sheet
                  class="calendar-header"
                  height="40"
                  dark
                  color="accent"
                >
                  <v-btn fab text small color="white" @click="prev">
                    <v-icon small>
                      mdi-chevron-left
                    </v-icon>
                  </v-btn>
                  <v-toolbar-title class="calendar-month-title">
                    {{ $refs.calendar ? $refs.calendar.title : '' }}
                  </v-toolbar-title>
                  <v-btn fab text small color="white" @click="next">
                    <v-icon small>
                      mdi-chevron-right
                    </v-icon>
                  </v-btn>
                  <v-spacer />
                  <!-- Calendar Type Selector -->
                  <div v-if="!$vuetify.breakpoint.xs">
                    <v-btn
                      :outlined="type !== 'day'"
                      @click="type = 'day'"
                      color="white"
                      light
                      v-if="!$vuetify.breakpoint.xs"
                      class="type-button mr-2"
                    >
                      {{ $t('calendar.daily') }}
                    </v-btn>
                    <v-btn
                      :outlined="type !== 'week'"
                      @click="type = 'week'"
                      color="white"
                      light
                      class="type-button mr-2"
                    >
                      {{ $t('calendar.weekly') }}
                    </v-btn>
                    <v-btn
                      :outlined="type !== 'month'"
                      @click="type = 'month'"
                      color="white"
                      light
                      class="type-button"
                    >
                      {{ $t('calendar.monthly') }}
                    </v-btn>
                  </div>
                  <v-menu bottom left v-else>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" dark>
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item>
                        <v-list-item-title @click="type = 'day'">
                          {{ $t('calendar.daily') }}
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-title @click="type = 'week'">
                          {{ $t('calendar.weekly') }}
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-title @click="type = 'month'">
                          {{ $t('calendar.monthly') }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-sheet>
                <v-sheet class="calendar-body">
                  <v-calendar
                    v-model="focus"
                    ref="calendar"
                    :now="today"
                    :value="today"
                    :events="events"
                    @click:event="showEvent"
                    @click:more="viewDay"
                    color="secondary"
                    event-color="secondary"
                    event-more-text="Ver {0} más"
                    :type="type"
                    :locale="locale"
                  ></v-calendar>
                  <v-menu
                    v-model="selectedOpen"
                    :close-on-content-click="false"
                    :activator="selectedElement"
                    offset-y
                    max-width="650px"
                  >
                    <v-card
                      color="grey lighten-4 event-detail"
                      min-width="350px"
                      max-width="650px"
                      flat
                    >
                      <v-toolbar color="secondary" dark>
                        <v-toolbar-title
                          v-html="selectedEvent.name"
                          :title="selectedEvent.name"
                        ></v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              @click="editEvent"
                              v-bind="attrs"
                              v-on="on"
                              v-if="isAdmin"
                            >
                              <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('common.edit') }}</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              @click="deleteEvent"
                              v-bind="attrs"
                              v-on="on"
                              v-if="isAdmin"
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('common.delete') }}</span>
                        </v-tooltip>
                      </v-toolbar>
                      <v-card-text>
                        <p v-if="getOneDate()">
                          <span class="bullet-label"
                            >{{ $t('calendar.date') }}:
                          </span>
                          <strong>{{ getOneDate() }}</strong>
                        </p>
                        <p>
                          <span class="bullet-label"
                            >{{ $t('calendar.start') }}:
                          </span>
                          <strong>{{ getStartDate() }}</strong>
                        </p>
                        <p>
                          <span class="bullet-label"
                            >{{ $t('calendar.end') }}:
                          </span>
                          <strong>{{ getEndDate() }}</strong>
                        </p>
                        <p
                          v-if="
                            selectedEvent.leaders &&
                              selectedEvent.leaders.length
                          "
                        >
                          <span class="bullet-label">
                            {{
                              selectedEvent.leaders.length > 1
                                ? $t('calendar.leaders') + ':'
                                : $t('calendar.leader') + ':'
                            }}
                          </span>
                          <span
                            :class="
                              selectedEvent.leaders.length > 1
                                ? 'leaders-list'
                                : ''
                            "
                            v-html="selectedEvent.leadersHtml"
                          />
                        </p>
                        <p v-if="selectedEvent.description" class="no-margin">
                          <span class="bullet-label">
                            {{ $t('calendar.description') }}:
                          </span>
                          <span>{{ selectedEvent.description }}</span>
                        </p>
                      </v-card-text>
                      <v-divider />
                      <v-card-actions>
                        <v-btn
                          text
                          color="secondary"
                          @click="selectedOpen = false"
                        >
                          {{ $t('common.close') }}
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-menu>
                </v-sheet>
              </v-col>
              <!-- EVENT FORM -->
              <v-col v-else>
                <v-sheet
                  :width="$vuetify.breakpoint.xs ? '100%' : '500'"
                  class="event-form"
                >
                  <v-row>
                    <v-col cols="12">
                      <h3 class="event-form-title">
                        {{ $t('calendar.newEventTitle') }}
                      </h3>
                    </v-col>
                    <!-- Name -->
                    <v-col cols="12" md="12">
                      <v-text-field
                        v-model="event.name"
                        :label="nameText"
                        required
                        autofocus
                        hide-details="true"
                        color="secondary"
                        maxlength="50"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-autocomplete
                        v-model="event.leaders"
                        :items="leaders"
                        :item-text="
                          item =>
                            `${item.name} ${item.lastname} (${item.country}, ${item.city})`
                        "
                        item-value="id"
                        chips
                        deletable-chips
                        multiple
                        small-chips
                        hide-details="true"
                        :label="assoLeadersText"
                        :hide-no-data="true"
                      >
                      </v-autocomplete>
                    </v-col>
                    <!-- Start date -->
                    <v-col cols="12" md="6">
                      <v-menu
                        ref="startDatePicker"
                        v-model="startDatePicker"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="event.startDate"
                            :label="startDateText"
                            color="secondary"
                            prepend-icon="mdi-calendar"
                            readonly
                            hide-details="true"
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="event.startDate"
                          no-title
                          scrollable
                          :locale="locale"
                          @input="
                            startDatePicker = false
                            validateStartDate()
                          "
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <!-- Start Time -->
                    <v-col cols="12" md="6">
                      <v-menu
                        ref="startTimePicker"
                        v-model="startTimePicker"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        :return-value.sync="event.startTime"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="event.startTime"
                            :label="startTimeText"
                            prepend-icon="mdi-clock-time-four-outline"
                            readonly
                            hide-details="true"
                            color="secondary"
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-time-picker
                          v-if="startTimePicker"
                          v-model="event.startTime"
                          full-width
                          color="secondary"
                          @click:minute="
                            $refs.startTimePicker.save(event.startTime)
                            validateStartTime()
                          "
                          format="24hr"
                        ></v-time-picker>
                      </v-menu>
                    </v-col>
                    <!-- End date -->
                    <v-col cols="12" md="6">
                      <v-menu
                        ref="endDatePicker"
                        v-model="endDatePicker"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="event.endDate"
                            :label="endDateText"
                            color="secondary"
                            prepend-icon="mdi-calendar"
                            readonly
                            hide-details="true"
                            v-bind="attrs"
                            v-on="on"
                            :class="endDateInputClasses + ' hola'"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="event.endDate"
                          :min="event.startDate"
                          no-title
                          :locale="locale"
                          scrollable
                          @input="endDatePicker = false"
                        >
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                    <!-- End Time -->
                    <v-col cols="12" md="6">
                      <v-menu
                        ref="endTimePicker"
                        v-model="endTimePicker"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        :return-value.sync="event.endTime"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="event.endTime"
                            :label="endTimeText"
                            prepend-icon="mdi-clock-time-four-outline"
                            readonly
                            hide-details="true"
                            color="secondary"
                            v-bind="attrs"
                            v-on="on"
                            :class="endTimeInputClasses"
                          ></v-text-field>
                        </template>
                        <v-time-picker
                          v-if="endTimePicker"
                          v-model="event.endTime"
                          full-width
                          color="secondary"
                          @click:minute="
                            $refs.endTimePicker.save(event.endTime)
                          "
                          format="24hr"
                          :min="minEndTime"
                        ></v-time-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="event.description"
                        rows="4"
                        color="secondary"
                        hide-details="true"
                      >
                        <template v-slot:label>
                          <div>
                            {{ $t('calendar.description') }}
                            <small>{{ $t('reg.additionalInfo') }} </small>
                          </div>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
            </div>

            <v-row justify="center">
              <v-btn
                class="mr-4 mt-4"
                @click="closeDialog"
                v-show="!showEventForm"
              >
                {{ $t('common.close') }}
              </v-btn>
              <v-btn
                color="secondary"
                class="mr-4 mt-4"
                @click="openEventForm"
                v-show="!showEventForm"
                v-if="isAdmin"
              >
                {{ $t('calendar.createEvent') }}
              </v-btn>
              <v-btn
                class="mr-4 mt-4"
                v-show="showEventForm"
                @click="closeEventForm"
                v-if="isAdmin"
              >
                {{ $t('common.close') }}
              </v-btn>
              <v-btn
                color="secondary"
                v-show="showEventForm"
                class="mt-4"
                @click="saveEvent"
              >
                <span v-if="savingEvent"
                  ><v-progress-circular
                    indeterminate
                    color="secondary"
                    v-if="isAdmin"
                  ></v-progress-circular
                ></span>
                <span v-else>{{ $t('common.save') }}</span>
              </v-btn>
            </v-row>
          </div>
        </v-tab-item>
        <v-tab-item class="calendar-tab-content">
          <CalendarInforms />
        </v-tab-item>
      </v-tabs-items>
    </div>
  </v-dialog>
</template>

<script>
import _clone from 'lodash/clone'
import { mapState } from 'vuex'
import { getCalendarDate } from '@/utils/common'
import { auth } from '../firebase'
import { isUserAdmin } from '@/consts/common'
import CalendarInforms from '@/components/CalendarInforms'

export default {
  components: {
    CalendarInforms
  },
  data: () => ({
    isAdmin: isUserAdmin(auth.currentUser.email),
    focus: '',
    type: 'month',
    today: getCalendarDate(new Date()),
    selectedElement: null,
    selectedOpen: false,
    showInformView: false,
    tab: 0,
    //Form data:
    startDatePicker: false,
    startTimePicker: false,
    endDatePicker: false,
    endTimePicker: false,
    event: {},
    endDateInputClasses: '',
    endTimeInputClasses: '',
    locale: localStorage.getItem('bd-kol-lang') || 'es'
  }),
  watch: {
    openCalendarDialog(newVal) {
      if (newVal) {
        setTimeout(() => {
          this.prev()
        })
        setTimeout(() => {
          this.next()
        })
        this.$store.dispatch('eventStore/fetchEvents', this.leadersMap)
      } else {
        setTimeout(() => {
          this.tab = 0
        }, 500)
      }
    },
    events() {
      setTimeout(() => {
        this.prev()
      })
      setTimeout(() => {
        this.next()
      })
    }
  },
  computed: {
    ...mapState({
      openCalendarDialog: state => state.eventStore.openCalendarDialog,
      events: state => state.eventStore.events,
      loadingEvents: state => state.eventStore.loadingEvents,
      savingEvent: state => state.eventStore.savingEvent,
      showEventForm: state => state.eventStore.showEventForm,
      selectedEvent: state => state.eventStore.selectedEvent,
      leaders: state => state.leaderStore.leaders,
      leadersMap: state => state.leaderStore.leadersMap
    }),
    minEndTime: function() {
      return this.event.startDate === this.event.endDate
        ? this.event.startTime
        : '00:00'
    },
    nameText() {
      return this.$t('calendar.name')
    },
    assoLeadersText() {
      return this.$t('calendar.assoLeaders')
    },
    startDateText() {
      return this.$t('calendar.startDate')
    },
    startTimeText() {
      return this.$t('calendar.startTime')
    },
    endDateText() {
      return this.$t('calendar.endDate')
    },
    endTimeText() {
      return this.$t('calendar.endTime')
    },
    eventNameRequiredText() {
      return this.$t('alerts.eventNameRequired')
    }
  },
  methods: {
    closeDialog() {
      this.$store.dispatch('eventStore/openCalendarDialog', false)
      this.closeEventForm()
    },
    prev() {
      if (this.$refs.calendar) this.$refs.calendar.prev()
    },
    next() {
      if (this.$refs.calendar) this.$refs.calendar.next()
    },
    viewDay({ date }) {
      this.focus = date
      this.type = 'day'
    },
    showEvent({ nativeEvent, event }) {
      this.$store.dispatch('eventStore/selectEvent', event)
      this.selectedElement = nativeEvent.target
      setTimeout(() => {
        this.selectedOpen = true
      }, 10)

      nativeEvent.stopPropagation()
    },
    getOneDate() {
      return this.selectedEvent.startDate === this.selectedEvent.endDate
        ? this.selectedEvent.startDate
        : null
    },
    getStartDate() {
      const isOneDate = this.getOneDate()
      return isOneDate ? this.selectedEvent.startTime : this.selectedEvent.start
    },
    getEndDate() {
      const isOneDate = this.getOneDate()
      return isOneDate ? this.selectedEvent.endTime : this.selectedEvent.end
    },
    openEventForm() {
      this.$store.dispatch('eventStore/showEventForm', true)
      this.event = {
        name: '',
        description: '',
        startDate: getCalendarDate(new Date()),
        endDate: getCalendarDate(new Date()),
        startTime: '09:00',
        endTime: '10:00'
      }
    },
    editEvent() {
      this.$store.dispatch('eventStore/showEventForm', true)
      this.event = _clone(this.selectedEvent)
      this.selectedOpen = false
    },
    closeEventForm() {
      this.$store.dispatch('eventStore/showEventForm', false)
      setTimeout(() => {
        this.prev()
      })
      setTimeout(() => {
        this.next()
      })
    },
    validateStartDate() {
      this.event.endDate = this.event.startDate
      this.validateStartTime()
      this.endDateInputClasses = 'data-updated'
      setTimeout(() => {
        this.endDateInputClasses = ''
      }, 1200)
    },
    validateStartTime() {
      const startDate = new Date(
        this.event.startDate + ' ' + this.event.startTime
      )
      const endDate = new Date(this.event.endDate + ' ' + this.event.endTime)

      if (startDate.getTime() > endDate.getTime()) {
        let nextDate = new Date(startDate.getTime())
        nextDate.setHours(nextDate.getHours() + 1)
        this.event.endTime = getCalendarDate(nextDate, true).substr(10)
        this.endTimeInputClasses = 'data-updated'
        setTimeout(() => {
          this.endTimeInputClasses = ''
        }, 1200)
      }
    },
    saveEvent() {
      if (this.event.name) {
        if (this.event.id) {
          this.$store.dispatch('eventStore/updateEvent', {
            event: this.event,
            leadersMap: this.leadersMap
          })
        } else {
          this.$store.dispatch('eventStore/addEvent', {
            event: this.event,
            leadersMap: this.leadersMap
          })
        }
      } else {
        alert(this.eventNameRequiredText)
      }
    },
    deleteEvent() {
      this.$store.dispatch('eventStore/deleteEvent', this.selectedEvent)
      this.selectedOpen = false
    },
    showInforms() {
      this.showInformView = true
    }
  }
}
</script>
<style lang="scss">
.calendar-dialog {
  min-width: 340px;
}
.calendar-container {
  background-color: #fff;
  padding: 20px 32px;
  word-break: break-word;

  .calendar-tabs {
    width: 250px;
    margin-left: 25px;

    @media (max-width: 599px) {
      margin-left: 0;
    }
  }
  .calendar-tab-content {
    min-height: 570px;
  }
  .calendar-header {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;

    .calendar-month-title {
      min-width: 165px;
      text-align: center;
    }
    .type-button {
      color: #4ea8bf;
    }
  }
  .calendar-body {
    height: calc(100vh - 316px);
    min-height: 460px;
  }
  .v-calendar-weekly__head {
    border-bottom: 1px solid #f1f1f1;
  }
  .v-calendar-weekly__head-weekday {
    padding-top: 10px;
  }
  .v-calendar .v-event {
    margin-left: 5px;
  }
  .v-calendar .v-event-more {
    margin-left: 5px;
  }
  .v-calendar-weekly__day-label .v-btn--fab.v-size--small {
    opacity: 0.8;
  }
  .data-loading {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .event-form {
    margin: auto;
    width: 500px;
    padding: 20px;
    background: #e9f4fb;
    border-radius: 5px;

    .event-form-title {
      color: #1d5969;
    }
    .data-updated {
      .v-text-field__slot {
        background: #c4dfd0;
        input {
          font-weight: bold;
        }
      }
    }
  }
}
.event-detail {
  .bullet-label {
    background-color: #6586ab;
    color: white;
    padding: 0 5px;
    border-radius: 5px;
    min-width: 49px;
    display: inline-block;
    margin-right: 5px;
  }
  .leaders-list {
    display: block;
    padding-left: 15px;
  }
}

//Vuetify Fixes
.v-picker__title__btn--active {
  animation: blinker 1s linear infinite;
}
.v-picker__title__btn:not(.v-picker__title__btn--active) {
  opacity: 1;
}
</style>
